// client/src/pages/PollsPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../authentication/AuthContext';
import { pollsService } from '../services/pollsService';
import { Link } from 'react-router-dom';
import { 
  Box, Typography, Paper, Button, Accordion, AccordionSummary, 
  AccordionDetails, CircularProgress, Grid, 
  Chip, Stack
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../styles/Theme.js';
import PieChartComponent from '../components/PieChartComponent';
import '../styles/PollsPage.css';

function PollsPage() {
  const { user, isAdmin } = useAuth();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const pollsData = await pollsService.getAllPolls();
      setPolls(pollsData || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching polls:', err);
      setError('Failed to load polls. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Function to prepare data for pie chart
  const preparePieChartData = (poll) => {
    if (!poll.responses || Object.keys(poll.responses).length === 0) {
      return [];
    }

    // Count responses for each option
    const counts = {};
    Object.values(poll.responses).forEach(response => {
      const answer = response.response;
      counts[answer] = (counts[answer] || 0) + 1;
    });

    // Format for PieChartComponent
    return Object.entries(counts).map(([option, count]) => ({
      option,
      count
    }));
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <div className="polls-page">
        <Box sx={{ padding: 4, maxWidth: '1200px', margin: '0 auto' }}>
          <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
            Opinion Polls
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
            Explore what our diverse personas think about various topics. Each poll provides insights into different perspectives and viewpoints.
          </Typography>
                {user && isAdmin && (
                  <Button
                    variant="outlined"
                    component={Link}
                    to="/pollsadmin"
                    sx={{ mb: 3 }}
                  >
                    Manage Polls
                  </Button>
                )}

          {error && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#ffebee' }}>
              <Typography color="error">{error}</Typography>
            </Paper>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : polls.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6">No polls available yet</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Check back soon for new polls.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ mt: 2 }}>
              {polls.map((poll) => (
                <Accordion key={poll.pollId} sx={{ mb: 2, borderRadius: 1, overflow: 'hidden' }}>
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      backgroundColor: 'var(--primary-color)',
                      '&:hover': { backgroundColor: 'var(--secondary-color)' } 
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {poll.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Box sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                      <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                        Poll created on {new Date(poll.createdAt * 1000).toLocaleDateString()}
                      </Typography>
                      
                      {/* Poll Type Information */}
                      {poll.pollType && (
                        <Box sx={{ mb: 2 }}>
                          <Chip 
                            label={poll.pollType === 'multiple-choice' ? 'Multiple Choice' : 
                                  poll.pollType === 'yes-no' ? 'Yes/No' : 
                                  poll.pollType === 'rating' ? 'Rating' : 'Text'}
                            size="small"
                            color="primary"
                            sx={{ mr: 1 }}
                          />
                          
                          {poll.options && poll.options.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                Options:
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                {poll.options.map((option, i) => (
                                  <Chip key={i} label={option} size="small" variant="outlined" />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {poll.responses && Object.keys(poll.responses).length > 0 ? (
                        <Grid container spacing={3}>
                          {/* Chart Section */}
                          {(poll.pollType === 'multiple-choice' || poll.pollType === 'yes-no' || poll.pollType === 'rating') && (
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2, mb: 2, height: '100%' }}>
                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                                  Poll Results
                                </Typography>
                                <PieChartComponent 
                                  questionText={poll.question}
                                  responseCounts={preparePieChartData(poll)}
                                  height={300}
                                />
                              </Paper>
                            </Grid>
                          )}
                          
                          {/* Individual Responses Section */}
                          <Grid item xs={12} md={(poll.pollType === 'multiple-choice' || poll.pollType === 'yes-no' || poll.pollType === 'rating') ? 6 : 12}>
                            <Paper sx={{ p: 2, mb: 2, height: '100%' }}>
                              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                                Individual Responses
                              </Typography>
                              {Object.entries(poll.responses).map(([personaId, response], index) => (
                                <Paper key={personaId} sx={{ p: 2, mb: 2, borderLeft: '4px solid var(--tertiary-color)' }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {response.personaName}
                                  </Typography>
                                  <Typography variant="body2">
                                    {response.response}
                                  </Typography>
                                </Paper>
                              ))}
                            </Paper>
                          </Grid>
                        </Grid>
                      ) : (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                          {poll.status === 'pending' ? 'Responses are being generated...' : 'No responses yet'}
                        </Typography>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default PollsPage;